/**
 * DTO du joueur (DECISIONS §4.2 — section Joueur).
 */
import { z } from 'zod';
import { localeSchema, progressionSchema, rareteSchema, runResumeSchema, userSchema, } from './models.js';
import { cardEffectSchema, effetAmeliorationPermanenteSchema } from '../content/index.js';
/** Réponse de `GET /me` (`200`). */
export const meResponseSchema = z
    .object({ user: userSchema, progression: progressionSchema })
    .strict();
/** Réponse de `PATCH /me` (`200`). */
export const updateMeResponseSchema = z.object({ user: userSchema }).strict();
/** Carte possédée dans l'export RGPD (forme minimale, §11). */
export const cartePossedeeSchema = z
    .object({
    cardId: z.string(),
    obtainedAt: z.string().datetime(),
})
    .strict();
/**
 * Carte du **grimoire** (`GET /me/collection`, arbitrage A-27).
 *
 * Toutes les cartes du contenu publié sont listées, possédées ou non. Pour une carte **non
 * possédée**, `descKey`, `effects` et `tags` valent `null` : l'effet d'une carte verrouillée
 * ne transite jamais par le réseau avant son acquisition (F13, même règle que le manifeste).
 * `obtainedAt` est `null` tant que la carte n'est pas possédée.
 *
 * `points` est le barème de rareté **du serveur** (`balance.points_rarete`) : les points sont
 * cumulés et **non dépensables** (F15) — aucune route ne les accepte en entrée.
 */
export const carteGrimoireSchema = z
    .object({
    cardId: z.string(),
    rarity: rareteSchema,
    points: z.number().int().nonnegative(),
    nameKey: z.string(),
    possedee: z.boolean(),
    obtainedAt: z.string().datetime().nullable(),
    descKey: z.string().nullable(),
    effects: z.array(cardEffectSchema).nullable(),
    tags: z.array(z.string()).nullable(),
})
    .strict();
/** Réponse de `GET /me/collection` (`200`). */
export const collectionResponseSchema = z
    .object({
    /** **Toutes** les cartes du contenu publié, dans l'ordre du manifeste. */
    cards: z.array(carteGrimoireSchema),
    /** Somme des points de rareté des cartes possédées — **non dépensables** (F15). */
    pointsTotal: z.number().int().nonnegative(),
    pointsByRarity: z
        .object({
        commune: z.number().int().nonnegative(),
        rare: z.number().int().nonnegative(),
        legendaire: z.number().int().nonnegative(),
        mythique: z.number().int().nonnegative(),
    })
        .strict(),
})
    .strict();
/** Réponse de `GET /me/runs` (`200`). */
export const meRunsResponseSchema = z.object({ runs: z.array(runResumeSchema) }).strict();
/**
 * Amélioration permanente vue par le joueur : catalogue + niveau possédé.
 *
 * `niveau` 0 = jamais achetée. `coutProchain` est le prix du **niveau suivant**, calculé côté
 * serveur depuis `couts[niveau]` ; il vaut `null` au niveau maximum. L'`effet` est celui du
 * catalogue (public) : le résultat appliqué, lui, n'est renvoyé que dans `state` de la run.
 */
export const ameliorationJoueurSchema = z
    .object({
    upgradeId: z.string().min(1),
    niveau: z.number().int().nonnegative(),
    /** Coût du niveau suivant, `null` si le niveau maximum est atteint. */
    coutProchain: z.number().int().nonnegative().nullable(),
    effet: effetAmeliorationPermanenteSchema,
})
    .strict();
/** Réponse de `GET /me/upgrades` (`200`) : catalogue complet + état du joueur. */
export const meUpgradesResponseSchema = z
    .object({
    /** Solde d'éclats persistant, source de vérité serveur. */
    eclats: z.number().int().nonnegative(),
    upgrades: z.array(ameliorationJoueurSchema),
})
    .strict();
/** Réponse de `POST /me/upgrades/:upgradeId` (`200`) : amélioration mise à jour + nouveau solde. */
export const upgradeBuyResponseSchema = z
    .object({
    upgrade: ameliorationJoueurSchema,
    eclats: z.number().int().nonnegative(),
})
    .strict();
/**
 * Profil du joueur dans l'export RGPD (`GET /me/export`, §11).
 *
 * Champs **minimisés** : ce que la personne a le droit de retrouver sur elle-même, et rien
 * de plus. Aucun identifiant de session, aucun hash, aucun rôle technique.
 * `emailVerifiedAt` est la date de vérification (ou `null`), pas un booléen : une date est
 * une donnée personnelle exportable, un booléen dérivé ne l'est pas.
 */
export const exportProfilSchema = z
    .object({
    pseudo: z.string(),
    email: z.string(),
    locale: localeSchema,
    createdAt: z.string().datetime(),
    emailVerifiedAt: z.string().datetime().nullable(),
})
    .strict();
/**
 * Progression dans l'export RGPD.
 *
 * `pointsCollection` est **recalculé par le serveur** à partir des cartes réellement
 * possédées et du barème publié : l'export ne recopie pas un total stocké qui pourrait
 * dériver (même règle que `GET /me/collection`). Les améliorations permanentes sont
 * listées niveau par niveau ; `eclats` est le solde dépensable.
 */
export const exportProgressionSchema = z
    .object({
    niveauMax: z.number().int().nonnegative(),
    eclats: z.number().int().nonnegative(),
    pointsCollection: z.number().int().nonnegative(),
    upgrades: z.array(z
        .object({
        upgradeId: z.string().min(1),
        niveau: z.number().int().nonnegative(),
    })
        .strict()),
    collection: z.array(cartePossedeeSchema),
})
    .strict();
/** Preuve de consentement exportée : nature, version du document, date d'acceptation. */
export const exportConsentementSchema = z
    .object({
    kind: z.enum(['cgu', 'confidentialite', 'cookies_analytics']),
    version: z.string(),
    acceptedAt: z.string().datetime(),
})
    .strict();
/**
 * Contenu de `GET /me/export` (RGPD, M4).
 *
 * L'export est un JSON complet mais **minimisé** : aucune donnée technique inutile
 * (hash de mot de passe, hash d'IP, jeton de session, empreinte de refresh, `requestId`
 * interne). Le nombre de runs est borné par la requête (`limit`, 100 par défaut, 500 au
 * maximum) : un export ne peut pas devenir un vecteur de surcharge.
 *
 * **Aucun champ de ce schéma n'est accepté en entrée** : cette structure est sortante
 * uniquement, et le contrôleur ne lit aucun identifiant de joueur dans la requête.
 */
export const meExportSchema = z
    .object({
    exportedAt: z.string().datetime(),
    profile: exportProfilSchema,
    progression: exportProgressionSchema,
    consents: z.array(exportConsentementSchema),
    runs: z.array(runResumeSchema),
})
    .strict();
//# sourceMappingURL=me.js.map